import { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';
import { GridValidRowModel } from '@mui/x-data-grid/models/gridRows';

import { formatPercentage, newGridRecordId } from '@shared/commonFunctions';
import { dataGridCol, gridCurrencyCell } from '@shared/components/grid/common';
import { GridColSelect } from '@shared/components/grid/GridColSelect';
import { GridColNumber } from '@shared/components/grid/GridColNumber';
import { statesList, getStateLabel } from '@shared/constants/statesList';
import { operatorsList } from '@shared/constants/operatorsList';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { GridColTextInput } from '@shared/components/grid/GridColTextInput';
import { LocalTabContent } from '@styles/CommonStyles';

import { stateRulesValidation } from '../General/validation';
import { ILenderUtilityTabProps } from '../LenderUtilityTabs';
import { bureausList } from '../../shared/data';
import { requestCreateBureau, requestDeleteBureau, requestUpdateBureau } from './queries';

const columns: GridColDef[] = [
  dataGridCol('state', {
    headerName: 'Lender State',
    renderEditCell: (params) => <GridColSelect isSearchable params={params} options={statesList} />,
  }),
  dataGridCol('stateName', 
  {
    valueGetter: ({ row }) => row.state, 
    renderCell: ({ row }) => getStateLabel(row.state),
    renderEditCell: ({ row }) => getStateLabel(row.state),
  }),
  dataGridCol('zips', {
    renderEditCell: (params) => <GridColTextInput isMultipleZipInput params={params} />,
  }),
  dataGridCol('bureau', {
    renderEditCell: (params) => <GridColSelect params={params} options={bureausList} />,
  }),
  dataGridCol('daysToFirstPayment', {
    renderEditCell: (params) => <GridColNumber params={params} />,
  }),
  dataGridCol('loanAmount', {
    ...gridCurrencyCell,
    renderEditCell: (params) => <GridColNumber params={params} prefix="$" decimalScale={2} />,
  }),
  dataGridCol('loanAmountOperator', {
    renderEditCell: (params) => <GridColSelect params={params} options={operatorsList} />,
  }),
  dataGridCol('apr', {
    renderEditCell: (params) => <GridColNumber params={params} suffix="%" decimalScale={2} />,
    valueFormatter: (params) => params?.value ? formatPercentage(params?.value) : '',
  }),
  dataGridCol('aprOperator', {
    renderEditCell: (params) => <GridColSelect params={params} options={operatorsList} />,
  }),
  dataGridCol('rate', {
    renderEditCell: (params) => <GridColNumber params={params} suffix="%" decimalScale={2} />,
    valueFormatter: (params) => params?.value ? formatPercentage(params?.value) : '',
  }),
  dataGridCol('rateOperator', {
    renderEditCell: (params) => <GridColSelect params={params} options={operatorsList} />,
  }),
].map((column) => ({ editable: true, minWidth: 140, flex: 1, ...column }));

export const StateRules = ({ data, lenderId }: ILenderUtilityTabProps) => {
  const gridActions = useMemo(
    () => ({
      onSave: (row: GridValidRowModel) => {
        const data = {
          ...row,
          loanAmountOperator: !row.loanAmountOperator ? null : row.loanAmountOperator,
          aprOperator: !row.aprOperator ? null : row.aprOperator,
          rateOperator: !row.rateOperator ? null : row.rateOperator,
          bureau: !row.bureau ? null : row.bureau,
        };

        if (row?.id === newGridRecordId) {
          requestCreateBureau(lenderId, data);
        } else {
          requestUpdateBureau(lenderId, data);
        }
      },
      onDelete: (row: GridValidRowModel) => requestDeleteBureau(lenderId, row?.id),
    }),
    [lenderId]
  );

  const mappedStates = useMemo(
    () => (data?.stateRules ?? []).map((state) => ({
      ...state,
      zips: state?.zips?.join(', '),
    })),
    [data?.stateRules]
  );

  return useMemo(
    () => (
      <LocalTabContent>
        <PhoenixGrid
          autoHeight
          withHeader
          name="State Rule"
          gridId={`State Rule-${lenderId}`}
          columns={columns}
          loading={!mappedStates}
          pageSize={10}
          rows={mappedStates}
          actions={gridActions}
          validateSchema={stateRulesValidation}
        />
      </LocalTabContent>
    ),
    [mappedStates],
  );
};
